import { useEffect, useState } from "react";
import type { PageSharee, PageVisibility } from "../../shared/types";
import { Dialog } from "./Dialog";
import { MultiSelect } from "./MultiSelect";
import { PAGE_VISIBILITY_LABEL } from "./PageVisibility";
import { Tabs } from "./Tabs";
import { Button, ErrorText, FieldGroup } from "./ui";
import { get, put } from "../lib/api";

type Sharing<V extends PageVisibility> = { visibility: V; shares: PageSharee[]; candidates: PageSharee[] };

/**
 * The owner's control over who besides them may open a page or a file (security plan D1 and D16): nobody else, named
 * people and agents, everyone on the platform — and, for a page, anyone with the link. `url` is the thing's sharing
 * route (`/api/pages/<id>/sharing`, `/api/files/<id>/sharing`), read when it opens and written by Save.
 */
export function ShareDialog<V extends PageVisibility>({ url, title, visibilities, open, onClose, onSaved }: {
  url: string;
  title: string;
  visibilities: V[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [state, setState] = useState<Sharing<V> | null>(null);
  const [visibility, setVisibility] = useState<V>(visibilities[0]!);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    get<Sharing<V>>(url).then(
      (s) => {
        setState(s);
        setVisibility(s.visibility);
        setUserIds(s.shares.map((x) => x.userId));
      },
      (err) => setError(err.message),
    );
  }, [open, url]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await put(url, { visibility, userIds });
      onSaved();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      busy={saving}
      primary={<Button variant="primary" disabled={!state || saving} onClick={save}>{saving ? "Saving…" : "Save"}</Button>}
    >
      {error && <ErrorText error={error} />}
      {state && (
        <div className="space-y-4">
          <FieldGroup label="Who can open it" hint={PAGE_VISIBILITY_LABEL[visibility].means}>
            <Tabs label="Who can open it" value={visibility} onChange={setVisibility} tabs={visibilities.map((v) => ({ key: v, label: PAGE_VISIBILITY_LABEL[v].label }))} />
          </FieldGroup>
          {visibility === "private" && (
            <FieldGroup label="Also shared with" hint="People and agents who may open it beside you.">
              <MultiSelect
                wide
                placeholder="Nobody else"
                options={state.candidates.map((c) => ({ value: c.userId, label: c.name, hint: c.kind === "agent" ? "agent" : undefined }))}
                selected={userIds}
                onChange={setUserIds}
              />
            </FieldGroup>
          )}
        </div>
      )}
    </Dialog>
  );
}
